"use client";

import Image from "next/image";
import { m } from "framer-motion";
import TransitionLink from "./transitionLink";
import { ContentButton } from "./button";

export interface CardProps {
  icon?: { asset: { url: string }; alt?: string };
  letter?: string;
  title: string;
  text: string;
  variant?: "frame" | "filled";
  compact?: boolean;
  link?: { href: string; label: string };
  fillColumn?: boolean;
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

function getCardIconAlt(icon: CardProps["icon"], title: string) {
  if (typeof icon?.alt === "string" && icon.alt.trim().length > 0) {
    return icon.alt;
  }

  return `${title} icon`;
}

export default function Card({
  icon,
  letter,
  title,
  text,
  variant = "frame",
  compact = false,
  link,
  fillColumn = false,
}: CardProps) {
  let base: string;
  if (compact) {
    base = "flex flex-col items-start gap-[10px] p-4 sm:p-5 aspect-square max-w-[300px] min-w-0 justify-evenly sm:overflow-hidden";
  } else if (link) {
    base = "flex flex-col items-start gap-11 py-12 px-[60px] w-full h-full min-w-0";
  } else {
    const widthClass =
      variant === "filled"
        ? "flex-1"
        : fillColumn
          ? "max-w-[400px] justify-self-center"
          : "max-w-[350px]";
    const shapeClass =
      variant === "filled" ? "" : `aspect-[4/3] ${letter ? "justify-start" : "justify-between"}`;
    base = `flex ${shapeClass} flex-col items-start gap-4 sm:gap-6 px-6 py-12 sm:px-8 sm:py-14 lg:px-6 xl:px-8 w-full self-stretch min-w-0 ${widthClass}`;
  }
  const variantClass = variant === "filled" ? "bg-surface" : "frame";

  return (
    <m.div
      variants={itemVariants}
      className={`${base} ${variantClass}`}
    >
      {icon && (
        <Image
          src={icon.asset.url}
          alt={getCardIconAlt(icon, title)}
          aria-hidden={icon.alt ? undefined : true}
          width={140}
          height={140}
          className="w-[18%] max-w-12 aspect-square object-contain"
          unoptimized
        />
      )}
      {letter && !icon && (
        <span className="h1 text-primary leading-none">{letter}</span>
      )}
      <div className="flex min-w-0 max-w-full flex-col gap-[10px]">
        <div className="h4 max-w-full uppercase leading-tight break-words lg:!text-2xl xl:!text-3xl">{title}</div>
        <p className="font-sans text-base leading-normal">{text}</p>
      </div>
      {link && (
        <TransitionLink classes="btn btn-arrow group relative overflow-hidden" href={link.href}>
          <ContentButton>{link.label}</ContentButton>
        </TransitionLink>
      )}
    </m.div>
  );
}
